// frontend/src/components/ClientUpdatesPanel.jsx
/**
 * Client Updates & Activity Panel
 * Shows updates posted to a client and allows posting new updates
 * Sends email notifications to assigned owners via Resend
 */

import React, { useState, useEffect } from 'react';
import { apiGet, apiPost, apiDelete } from '../api';

export default function ClientUpdatesPanel({ clientId, user, onClose }) {
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newUpdate, setNewUpdate] = useState('');
  const [posting, setPosting] = useState(false);
  const [mentionSuggestions, setMentionSuggestions] = useState([]);
  const [showMentions, setShowMentions] = useState(false);
  const [employees, setEmployees] = useState([]);

  // Load updates and employees
  useEffect(() => {
    loadUpdates();
    loadEmployees();
  }, [clientId]);

  const loadUpdates = async () => {
    try {
      setLoading(true);
      const response = await apiGet(`/api/client-updates/by_client/${clientId}/`);
      setUpdates(Array.isArray(response) ? response : response.data || []);
    } catch (error) {
      console.error('Failed to load updates:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadEmployees = async () => {
    try {
      const response = await apiGet('/api/employees/');
      setEmployees(Array.isArray(response) ? response : response.data || []);
    } catch (error) {
      console.error('Failed to load employees:', error);
    }
  };

  const handlePost = async (e) => {
    e.preventDefault();

    if (!newUpdate.trim()) return;

    try {
      setPosting(true);

      // Extract @mentions from the text with debugging
      const mentionRegex = /@(\w+)/g;
      const mentions = [];
      let match;

      console.log("🔍 Extracting mentions from:", newUpdate);
      console.log("📋 Available employees:", employees);

      while ((match = mentionRegex.exec(newUpdate)) !== null) {
        const username = match[1];
        console.log(`  Looking for username: @${username}`);

        const employee = employees.find((emp) => {
          const matchUsername =
            emp.username?.toLowerCase() === username.toLowerCase();
          const matchFullName = emp.full_name
            ?.toLowerCase()
            .includes(username.toLowerCase());
          const matchFirstName =
            emp.first_name?.toLowerCase() === username.toLowerCase();
          const matchLastName =
            emp.last_name?.toLowerCase() === username.toLowerCase();

          console.log(`    Checking ${emp.username}:`, {
            username: emp.username,
            full_name: emp.full_name,
            user_id: emp.user_id,
            matchUsername,
            matchFullName,
            matchFirstName,
            matchLastName,
          });

          return (
            matchUsername || matchFullName || matchFirstName || matchLastName
          );
        });

        if (employee) {
          console.log(`  ✅ Found employee:`, employee);
          console.log(`     user_id: ${employee.user_id}`);

          if (employee.user_id) {
            mentions.push(employee.user_id);
            console.log(`  ✅ Added user_id ${employee.user_id} to mentions`);
          } else {
            console.log(`  ⚠️  Employee found but no user_id!`, employee);
          }
        } else {
          console.log(`  ❌ No employee found for @${username}`);
        }
      }

      console.log("📧 Final mentioned_user_ids:", mentions);

      const payload = {
        client: clientId,
        content: newUpdate,
        mentioned_user_ids: mentions,
      };

      console.log("📤 Posting payload:", payload);

      const response = await apiPost("/api/client-updates/", payload);
      console.log("✅ Response:", response);

      // Reload updates
      await loadUpdates();

      // Clear form
      setNewUpdate("");
    } catch (error) {
      console.error("❌ Failed to post update:", error);
      if (error.response) {
        console.error("Error response:", error.response.data);
      }
      alert("Failed to post update. Please try again.");
    } finally {
      setPosting(false);
    }
  };

  const handleDelete = async (updateId) => {
    if (!window.confirm('Delete this update?')) return;

    try {
      await apiDelete(`/api/client-updates/${updateId}/`);
      await loadUpdates();
    } catch (error) {
      console.error('Failed to delete update:', error);
      alert('Failed to delete update.');
    }
  };

  const handleTextChange = (e) => {
    const value = e.target.value;
    setNewUpdate(value);

    // Check for @ mentions
    const cursorPos = e.target.selectionStart;
    const textBeforeCursor = value.slice(0, cursorPos);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');
    
    if (lastAtIndex !== -1 && lastAtIndex === cursorPos - 1) {
      // Just typed @, show all employees
      setMentionSuggestions(employees);
      setShowMentions(true);
    } else if (lastAtIndex !== -1) {
      // Typing after @, filter employees
      const query = textBeforeCursor.slice(lastAtIndex + 1).toLowerCase();
      const filtered = employees.filter(emp =>
        emp.username?.toLowerCase().includes(query) ||
        emp.full_name?.toLowerCase().includes(query) ||  // FIXED: changed from 'name' to 'full_name'
        emp.first_name?.toLowerCase().includes(query) ||
        emp.last_name?.toLowerCase().includes(query)
      );
      setMentionSuggestions(filtered);
      setShowMentions(filtered.length > 0);
    } else {
      setShowMentions(false);
    }
  };

  const insertMention = (employee) => {
    const cursorPos = newUpdate.lastIndexOf('@');
    const before = newUpdate.slice(0, cursorPos);
    const after = newUpdate.slice(cursorPos + 1);
    const username = employee.username || employee.full_name.split(' ')[0];  // FIXED: changed from 'name' to 'full_name'
    
    setNewUpdate(`${before}@${username} ${after.replace(/^\S+\s*/, '')}`);
    setShowMentions(false);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      right: 0,
      width: 400,
      height: '100vh',
      background: 'white',
      borderLeft: '1px solid #e5e7eb',
      display: 'flex',
      flexDirection: 'column',
      zIndex: 1000,
      boxShadow: '-4px 0 20px rgba(0,0,0,0.1)',
    }}>
      {/* Header */}
      <div style={{
        padding: '16px 20px',
        borderBottom: '1px solid #e5e7eb',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}>
        <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>
          Updates & Activity
        </h3>
        <button
          onClick={onClose}
          style={{
            background: 'transparent',
            border: 'none',
            fontSize: 24,
            cursor: 'pointer',
            color: '#6b7280',
            padding: 0,
            lineHeight: 1,
          }}
        >
          ×
        </button>
      </div>

      {/* New Update Form */}
      <form onSubmit={handlePost} style={{
        padding: 20,
        borderBottom: '1px solid #e5e7eb',
        position: 'relative',
      }}>
        <textarea
          value={newUpdate}
          onChange={handleTextChange}
          placeholder="Write an update... (use @ to mention someone)"
          style={{
            width: '100%',
            minHeight: 80,
            padding: 12,
            border: '1px solid #d1d5db',
            borderRadius: 8,
            fontSize: 14,
            fontFamily: 'inherit',
            resize: 'vertical',
          }}
          disabled={posting}
        />

        {/* Mention Suggestions */}
        {showMentions && mentionSuggestions.length > 0 && (
          <div style={{
            position: 'absolute',
            bottom: 90,
            left: 20,
            right: 20,
            background: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: 8,
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            maxHeight: 200,
            overflowY: 'auto',
            zIndex: 10,
          }}>
            {mentionSuggestions.map((emp) => (
              <div
                key={emp.id}
                onClick={() => insertMention(emp)}
                style={{
                  padding: '8px 12px',
                  cursor: 'pointer',
                  fontSize: 14,
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#f3f4f6'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
              >
                <div style={{ fontWeight: 600 }}>{emp.full_name || emp.username}</div>
                <div style={{ fontSize: 12, color: '#6b7280' }}>@{emp.username}</div>
              </div>
            ))}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
          <span style={{ fontSize: 12, color: '#6b7280' }}>
            Ctrl+Enter to post
          </span>
          <button
            type="submit"
            disabled={posting || !newUpdate.trim()}
            style={{
              padding: '8px 16px',
              background: posting || !newUpdate.trim() ? '#d1d5db' : '#2563eb',
              color: 'white',
              border: 'none',
              borderRadius: 6,
              fontSize: 14,
              fontWeight: 600,
              cursor: posting || !newUpdate.trim() ? 'not-allowed' : 'pointer',
            }}
          >
            {posting ? 'Posting...' : 'Post Update'}
          </button>
        </div>
      </form>

      {/* Updates List */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
        {loading ? (
          <div style={{ textAlign: 'center', color: '#6b7280', padding: 40 }}>
            Loading updates...
          </div>
        ) : updates.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#6b7280', padding: 40 }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>💬</div>
            <div style={{ fontSize: 14 }}>No updates yet</div>
            <div style={{ fontSize: 12, marginTop: 4 }}>
              Be the first to add an update!
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {updates.map((update) => (
              <div key={update.id} style={{
                padding: 16,
                background: '#f9fafb',
                borderRadius: 8,
                border: '1px solid #e5e7eb',
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: 8,
                }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 14, color: '#111827' }}>
                      {update.author_name}
                    </div>
                    <div style={{ fontSize: 12, color: '#6b7280' }}>
                      {formatDate(update.created_at)}
                    </div>
                  </div>
                  
                  {user && update.author === user.id && (
                    <button
                      onClick={() => handleDelete(update.id)}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        color: '#dc2626',
                        cursor: 'pointer',
                        fontSize: 12,
                        padding: '4px 8px',
                      }}
                    >
                      Delete
                    </button>
                  )}
                </div>

                <div style={{
                  fontSize: 14,
                  color: '#374151',
                  lineHeight: 1.5,
                  whiteSpace: 'pre-wrap',
                }}>
                  {update.content}
                </div>

                {update.email_sent && (
                  <div style={{
                    marginTop: 8,
                    fontSize: 12,
                    color: '#059669',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                  }}>
                    <span>✓</span>
                    <span>Email notifications sent</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}